
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useUpdateProcurementStatus } from '@/hooks/use-procurement';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { sendTelegramNotification } from '@/services/telegram/send';
import { formatRejectedProcurementNotification } from '@/services/telegram/formatters';
import { getProcurementById } from '@/services/procurementService';

interface RejectConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  procurementId: string;
  onSuccess?: () => void;
}

const RejectConfirmationDialog: React.FC<RejectConfirmationDialogProps> = ({
  isOpen,
  onClose,
  procurementId,
  onSuccess
}) => {
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const { user } = useAuth();
  const updateProcurementMutation = useUpdateProcurementStatus();

  const handleReject = async () => {
    if (!user || !rejectionReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a rejection reason.",
        variant: "destructive",
      });
      return;
    }
    
    setIsRejecting(true);
    try {
      console.log('Starting rejection process for procurement:', procurementId);
      
      // Update procurement status to rejected - this will show in the rejected tab
      await updateProcurementMutation.mutateAsync({
        id: procurementId,
        status: 'rejected',
        rejection_source: 'finance',
        rejected_by: user.id,
        finance_rejected_at: new Date().toISOString(),
        rejection_reason: rejectionReason.trim()
      });

      console.log('Procurement status updated to rejected successfully');

      // Fetch complete enriched procurement data for notification
      console.log('Fetching enriched procurement data for notification...');
      const enrichedProcurement = await getProcurementById(procurementId);
      
      if (!enrichedProcurement) {
        console.error('Failed to fetch enriched procurement data');
        throw new Error('Failed to fetch procurement details for notification');
      }

      console.log('Enriched procurement data fetched:', {
        id: enrichedProcurement.id,
        items: enrichedProcurement.items?.length || 0,
        requestor: enrichedProcurement.requestor,
        totalAmount: enrichedProcurement.total_amount,
        sourceName: enrichedProcurement.source_name
      });

      // Send Telegram notification with the enriched procurement data and rejector info
      const notificationMessage = formatRejectedProcurementNotification(enrichedProcurement, user, rejectionReason.trim());
      console.log('Sending rejection notification with message preview:', notificationMessage.substring(0, 200) + '...');
      
      await sendTelegramNotification(
        notificationMessage,
        procurementId
      );

      toast({
        title: "Procurement Rejected",
        description: "The procurement has been rejected successfully.",
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error rejecting procurement:', error);
      toast({
        title: "Error",
        description: "Failed to reject procurement. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
    }
  };

  const handleClose = () => {
    setRejectionReason('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Procurement</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p>Are you sure you want to reject this procurement?</p>
          <div className="space-y-2">
            <Label htmlFor="rejection-reason">Rejection Reason *</Label>
            <Textarea
              id="rejection-reason"
              placeholder="Please provide a reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              disabled={isRejecting}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={handleClose} disabled={isRejecting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject} 
              disabled={isRejecting || !rejectionReason.trim()}
            >
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RejectConfirmationDialog;
